import { useMutation, useQueryClient } from "@tanstack/react-query";
import { GripVertical, Hash, Plus, X } from "lucide-react";
import { useCallback, useEffect, useId, useMemo, useState } from "react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { devLog } from "@/lib/env";
import type {
  ChecklistComponent,
  ComponentOption,
  ComponentType,
  PricingType,
} from "@/types/components";

interface ComponentBuilderProps {
  isOpen: boolean;
  onClose: () => void;
  checklistId: number;
  checklistName: string;
  existingComponents: ChecklistComponent[];
}

// Type definitions moved to @/types/components

export function ComponentBuilder({
  isOpen,
  onClose,
  checklistId,
  checklistName,
  existingComponents,
}: ComponentBuilderProps) {
  const [componentType, setComponentType] = useState<ComponentType>("text");
  const [label, setLabel] = useState("");
  const [placeholder, setPlaceholder] = useState("");
  const [helpText, setHelpText] = useState("");
  const [isRequired, setIsRequired] = useState(false);
  const [defaultValue, setDefaultValue] = useState("");
  const [options, setOptions] = useState<ComponentOption[]>([]);
  const [newOptionLabel, setNewOptionLabel] = useState("");
  const [newOptionPrice, setNewOptionPrice] = useState<string>("0");
  const [basePrice, setBasePrice] = useState<string>("0");
  const [pricingType, setPricingType] = useState<PricingType>("fixed");
  const [pricingUnitLabel, setPricingUnitLabel] = useState("");
  const [allowQuantityInput, setAllowQuantityInput] = useState(false);
  const [isNested, setIsNested] = useState(false);
  const [parentComponentId, setParentComponentId] = useState<number | null>(null);
  const [visibilityOption, setVisibilityOption] = useState<string>("");

  const queryClient = useQueryClient();
  const apiUrl = import.meta.env.VITE_CLOUDFLARE_API_URL || "http://localhost:8788";
  const baseId = useId();
  const componentTypeFieldId = `${baseId}-component-type`;
  const labelFieldId = `${baseId}-label`;
  const placeholderFieldId = `${baseId}-placeholder`;
  const helpTextFieldId = `${baseId}-help-text`;
  const defaultValueFieldId = `${baseId}-default-value`;
  const basePriceFieldId = `${baseId}-base-price`;
  const pricingTypeFieldId = `${baseId}-pricing-type`;
  const pricingUnitFieldId = `${baseId}-pricing-unit`;
  const requiredSwitchId = `${baseId}-required`;
  const quantitySwitchId = `${baseId}-quantity`;
  const nestedSwitchId = `${baseId}-nested-component`;
  const parentComponentFieldId = `${baseId}-parent-component`;
  const visibilityOptionFieldId = `${baseId}-visibility-option`;

  const flattenedComponents = useMemo(() => {
    const list: ChecklistComponent[] = [];

    const walk = (items: ChecklistComponent[] = [], depth = 0) => {
      items.forEach((component) => {
        list.push({ ...component, nested_level: component.nested_level ?? depth });
        if (component.children && component.children.length > 0) {
          walk(component.children, depth + 1);
        }
      });
    };

    walk(existingComponents || []);
    return list;
  }, [existingComponents]);

  const parentCandidates = useMemo(() => {
    return flattenedComponents.filter((component) => Boolean(component.id));
  }, [flattenedComponents]);

  const hasParentCandidates = parentCandidates.length > 0;

  const selectedParent = useMemo(() => {
    if (!parentComponentId) {
      return undefined;
    }
    return parentCandidates.find((component) => component.id === parentComponentId);
  }, [parentCandidates, parentComponentId]);

  const parentSupportsOptions = Boolean(
    selectedParent && ["select", "radio", "checkbox"].includes(selectedParent.component_type)
  );

  const resolveOptionValue = useCallback((option: ComponentOption): string => {
    if (option.option_value) {
      return option.option_value;
    }

    return option.option_label
      .toLowerCase()
      .replace(/\s+/g, "_")
      .replace(/[^a-z0-9_]/g, "");
  }, []);

  useEffect(() => {
    if (!isNested) {
      setParentComponentId(null);
      setVisibilityOption("");
    }
  }, [isNested]);

  useEffect(() => {
    if (!isNested) {
      return;
    }

    if (parentSupportsOptions && selectedParent?.options?.length) {
      setVisibilityOption((previous) => {
        if (previous) {
          return previous;
        }
        const firstOption = selectedParent.options?.[0];
        return firstOption ? resolveOptionValue(firstOption) : "";
      });
    } else if (!parentSupportsOptions) {
      setVisibilityOption("");
    }
  }, [isNested, parentSupportsOptions, selectedParent, resolveOptionValue]);

  interface ComponentOptionPayload {
    option_label: string;
    option_value: string;
    price_modifier: number;
    is_default: boolean;
    display_order: number;
  }

  interface CreateComponentPayload {
    checklist_id: number;
    component_type: ComponentType;
    field_name: string;
    label: string;
    placeholder?: string;
    help_text?: string;
    is_required: boolean;
    default_value?: string;
    base_price?: number;
    pricing_type?: PricingType;
    pricing_unit_label?: string;
    allow_quantity_input?: boolean;
    display_order: number;
    parent_component_id?: number | null;
    visibility_option?: string;
    visibility_condition?: string;
    nested_level?: number;
    options?: ComponentOptionPayload[];
  }

  const createComponentMutation = useMutation({
    mutationFn: async (data: CreateComponentPayload) => {
      devLog("Creating component with data:", data);
      const response = await fetch(`${apiUrl}/api/v1/checklists/components`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || "Failed to create component");
      }
      const result = await response.json();
      devLog("Component created successfully:", result);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["checklist-components", checklistId] });
      toast.success("Component added successfully");
      handleReset();
      onClose();
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : "Failed to add component";
      toast.error(message);
    },
  });

  const handleReset = () => {
    setLabel("");
    setPlaceholder("");
    setHelpText("");
    setIsRequired(false);
    setDefaultValue("");
    setOptions([]);
    setNewOptionLabel("");
    setNewOptionPrice("0");
    setBasePrice("0");
    setPricingType("fixed");
    setPricingUnitLabel("");
    setAllowQuantityInput(false);
    setIsNested(false);
    setParentComponentId(null);
    setVisibilityOption("");
  };

  const handleAddOption = () => {
    if (!newOptionLabel.trim()) {
      toast.error("Option label is required");
      return;
    }

    const trimmedLabel = newOptionLabel.trim();
    const slugify = (label: string) =>
      label
        .toLowerCase()
        .trim()
        .replace(/\s+/g, "_")
        .replace(/[^a-z0-9_]/g, "");

    const existingValues = new Set(
      options.map((option) => {
        if (option.option_value && option.option_value.trim() !== "") {
          return option.option_value;
        }
        return slugify(option.option_label || "");
      })
    );

    let optionValue = slugify(trimmedLabel);
    if (!optionValue) {
      optionValue = `option_${options.length + 1}`;
    }

    if (existingValues.has(optionValue)) {
      let suffix = 2;
      while (existingValues.has(`${optionValue}_${suffix}`)) {
        suffix += 1;
      }
      optionValue = `${optionValue}_${suffix}`;
    }

    const newOption: ComponentOption = {
      option_label: trimmedLabel,
      option_value: optionValue,
      price_modifier: Number.parseFloat(newOptionPrice) || 0,
      is_default: options.length === 0,
      display_order: options.length,
    };

    setOptions([...options, newOption]);
    setNewOptionLabel("");
    setNewOptionPrice("0");
  };

  const handleRemoveOption = (index: number) => {
    const newOptions = options.filter((_, i) => i !== index);
    // Update display_order for remaining options
    newOptions.forEach((opt, i) => {
      opt.display_order = i;
      if (i === 0 && !newOptions.some((o) => o.is_default)) {
        opt.is_default = true;
      }
    });
    setOptions(newOptions);
  };

  const handleSetDefault = (index: number) => {
    const newOptions = options.map((opt, i) => ({
      ...opt,
      is_default: i === index,
    }));
    setOptions(newOptions);
  };

  const computeDisplayOrder = () => {
    if (isNested && parentComponentId) {
      return flattenedComponents.filter(
        (component) => component.parent_component_id === parentComponentId
      ).length;
    }

    return flattenedComponents.filter((component) => !component.parent_component_id).length;
  };

  const handleSubmit = () => {
    if (!label.trim()) {
      toast.error("Label is required");
      return;
    }

    // For select, radio, and checkbox types, ensure there are options
    if (["select", "radio", "checkbox"].includes(componentType) && options.length === 0) {
      toast.error("Please add at least one option");
      return;
    }

    // Auto-generate field name from label - ensure it's not empty
    let fieldName = label
      .toLowerCase()
      .replace(/\s+/g, "_")
      .replace(/[^a-z0-9_]/g, "");
    if (!fieldName) {
      fieldName = `field_${Date.now()}`; // Fallback if no valid characters
    }

    let parentComponent: ChecklistComponent | undefined;
    if (isNested) {
      if (!parentComponentId) {
        toast.error("Select a parent component");
        return;
      }

      parentComponent = parentCandidates.find((component) => component.id === parentComponentId);
      if (!parentComponent) {
        toast.error("Parent component not found");
        return;
      }

      const parentHasOptions = ["select", "radio", "checkbox"].includes(
        parentComponent.component_type
      );

      if (parentHasOptions && !visibilityOption) {
        toast.error("Choose which parent option reveals this component");
        return;
      }
    }

    const displayOrder = computeDisplayOrder();
    const nestedLevel = parentComponent ? (parentComponent.nested_level ?? 0) + 1 : 0;

    let visibilityCondition: string | undefined;
    if (parentComponent) {
      const parentHasOptions = ["select", "radio", "checkbox"].includes(
        parentComponent.component_type
      );

      if (parentHasOptions) {
        const operator = parentComponent.component_type === "checkbox" ? "contains" : "equals";
        visibilityCondition = JSON.stringify({
          field: parentComponent.field_name,
          operator,
          value: visibilityOption,
        });
      } else {
        visibilityCondition = JSON.stringify({
          field: parentComponent.field_name,
          operator: "is_not_empty",
        });
      }
    }

    const componentData: CreateComponentPayload = {
      checklist_id: checklistId,
      component_type: componentType,
      field_name: fieldName,
      label: label.trim(),
      placeholder: placeholder.trim() || undefined,
      help_text: helpText.trim() || undefined,
      is_required: isRequired,
      display_order: displayOrder,
      default_value: defaultValue.trim() || undefined,
      base_price: Number.parseFloat(basePrice) || 0,
      pricing_type: pricingType,
      pricing_unit_label: pricingUnitLabel.trim() || undefined,
      allow_quantity_input: allowQuantityInput,
      parent_component_id: isNested ? parentComponentId : undefined,
      visibility_condition: visibilityCondition,
      nested_level: nestedLevel,
    };

    // Only add options for components that need them
    if (["select", "radio", "checkbox"].includes(componentType)) {
      componentData.options = options.map((option, index) => {
        const resolvedValue =
          option.option_value && option.option_value.trim() !== ""
            ? option.option_value
            : resolveOptionValue(option);
        return {
          option_label: option.option_label,
          option_value: resolvedValue,
          price_modifier: option.price_modifier || 0,
          is_default: option.is_default || index === 0,
          display_order: index,
        };
      });
    }

    devLog("Final component data being sent:", JSON.stringify(componentData, null, 2));
    createComponentMutation.mutate(componentData);
  };

  const requiresOptions = ["select", "radio", "checkbox"].includes(componentType);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add Component to Task</DialogTitle>
          <DialogDescription>Add a form component to "{checklistName}"</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {/* Component Type Selection */}
          <div>
            <Label htmlFor={componentTypeFieldId}>Component Type</Label>
            <Select
              value={componentType}
              onValueChange={(value) => setComponentType(value as ComponentType)}
            >
              <SelectTrigger id={componentTypeFieldId}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="text">Text Input</SelectItem>
                <SelectItem value="number">Number Input</SelectItem>
                <SelectItem value="textarea">Text Area</SelectItem>
                <SelectItem value="select">Select Dropdown</SelectItem>
                <SelectItem value="radio">Radio Buttons</SelectItem>
                <SelectItem value="checkbox">Checkboxes</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Basic Settings */}
          <div className="space-y-4">
            <div>
              <Label htmlFor={labelFieldId}>Label</Label>
              <Input
                id={labelFieldId}
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="e.g., Bathroom Size"
              />
            </div>

            <div>
              <Label htmlFor={placeholderFieldId}>Placeholder (Optional)</Label>
              <Input
                id={placeholderFieldId}
                value={placeholder}
                onChange={(e) => setPlaceholder(e.target.value)}
                placeholder="e.g., Enter bathroom dimensions..."
              />
            </div>

            <div>
              <Label htmlFor={helpTextFieldId}>Help Text (Optional)</Label>
              <Textarea
                id={helpTextFieldId}
                value={helpText}
                onChange={(e) => setHelpText(e.target.value)}
                placeholder="Additional instructions or information..."
                rows={2}
              />
            </div>

            {!requiresOptions && (
              <div>
                <Label htmlFor={defaultValueFieldId}>Default Value (Optional)</Label>
                <Input
                  id={defaultValueFieldId}
                  value={defaultValue}
                  onChange={(e) => setDefaultValue(e.target.value)}
                  placeholder="Default value for this field..."
                />
              </div>
            )}

            {/* Pricing Settings */}
            <div className="space-y-4 border-t pt-4">
              <h4 className="font-medium">Pricing Settings</h4>

              {!requiresOptions && (
                <div>
                  <Label htmlFor={basePriceFieldId}>Base Price ($)</Label>
                  <Input
                    id={basePriceFieldId}
                    type="number"
                    inputMode="decimal"
                    step="0.01"
                    value={basePrice}
                    onChange={(e) => setBasePrice(e.target.value)}
                    placeholder="0.00"
                  />
                </div>
              )}

              {!requiresOptions && (
                <div>
                  <Label htmlFor={pricingTypeFieldId}>Pricing Type</Label>
                  <Select
                    value={pricingType}
                    onValueChange={(value) => setPricingType(value as PricingType)}
                  >
                    <SelectTrigger id={pricingTypeFieldId}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fixed">Fixed Price</SelectItem>
                      <SelectItem value="per_unit">Per Unit</SelectItem>
                      <SelectItem value="per_character">Per Character</SelectItem>
                      <SelectItem value="custom">Custom Calculation</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {pricingType === "per_unit" && !requiresOptions && (
                <div>
                  <Label htmlFor={pricingUnitFieldId}>Unit Label</Label>
                  <Input
                    id={pricingUnitFieldId}
                    value={pricingUnitLabel}
                    onChange={(e) => setPricingUnitLabel(e.target.value)}
                    placeholder="e.g., per sq ft, per meter"
                  />
                </div>
              )}
            </div>

            <div className="flex items-center space-x-2">
              <Switch id={requiredSwitchId} checked={isRequired} onCheckedChange={setIsRequired} />
              <Label htmlFor={requiredSwitchId}>Required field</Label>
            </div>

            <div className="p-4 border rounded-lg bg-blue-50 border-blue-200">
              <div className="flex items-center space-x-2">
                <Switch
                  id={quantitySwitchId}
                  checked={allowQuantityInput}
                  onCheckedChange={setAllowQuantityInput}
                />
                <Label htmlFor={quantitySwitchId} className="flex items-center gap-2">
                  <Hash className="h-4 w-4" />
                  Allow Quantity Input
                </Label>
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                When enabled, estimators can specify quantities (e.g., "3 units of this item")
              </p>
            </div>
          </div>

          <div className="space-y-4 border-t pt-4">
            <h4 className="font-medium">Hierarchy &amp; Visibility</h4>
            <div className="flex items-center space-x-2">
              <Switch
                id={nestedSwitchId}
                checked={isNested}
                disabled={!hasParentCandidates}
                onCheckedChange={(checked) => setIsNested(checked)}
              />
              <Label htmlFor={nestedSwitchId}>
                Nest under an existing component
                {!hasParentCandidates && " (add a component first)"}
              </Label>
            </div>

            {isNested && (
              <div className="space-y-3">
                <div>
                  <Label htmlFor={parentComponentFieldId}>Parent Component</Label>
                  <Select
                    value={parentComponentId ? String(parentComponentId) : ""}
                    onValueChange={(value) => {
                      if (value) {
                        setParentComponentId(Number.parseInt(value, 10));
                      } else {
                        setParentComponentId(null);
                      }
                    }}
                  >
                    <SelectTrigger id={parentComponentFieldId}>
                      <SelectValue placeholder="Choose a parent component" />
                    </SelectTrigger>
                    <SelectContent>
                      {parentCandidates.map((component) => (
                        <SelectItem key={component.id} value={String(component.id)}>
                          {`${
                            component.nested_level && component.nested_level > 0
                              ? `${"-".repeat(component.nested_level)} `
                              : ""
                          }${component.label}${
                            component.nested_level && component.nested_level > 0
                              ? ` (Level ${component.nested_level + 1})`
                              : ""
                          }`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {parentSupportsOptions && selectedParent?.options?.length ? (
                  <div>
                    <Label htmlFor={visibilityOptionFieldId}>
                      Show when parent option is selected
                    </Label>
                    <Select value={visibilityOption} onValueChange={setVisibilityOption}>
                      <SelectTrigger id={visibilityOptionFieldId}>
                        <SelectValue placeholder="Choose parent option" />
                      </SelectTrigger>
                      <SelectContent>
                        {selectedParent.options?.map((option) => {
                          const optionValue = resolveOptionValue(option);
                          return (
                            <SelectItem
                              key={`${selectedParent.id}-${optionValue}`}
                              value={optionValue}
                            >
                              {option.option_label}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground mt-1">
                      The component only appears when the selected option is chosen.
                    </p>
                  </div>
                ) : null}

                {selectedParent && !parentSupportsOptions && (
                  <p className="text-xs text-muted-foreground">
                    This component will be shown whenever "{selectedParent.label}" has a value.
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Options Section for Select, Radio, and Checkbox */}
          {requiresOptions && (
            <div className="space-y-4">
              <div>
                <Label>Options</Label>
                <Card className="mt-2">
                  <CardContent className="p-4">
                    <div className="space-y-2">
                      {/* Add Option Form */}
                      <div className="space-y-2">
                        <div className="flex gap-2">
                          <Input
                            className="flex-1"
                            value={newOptionLabel}
                            onChange={(e) => setNewOptionLabel(e.target.value)}
                            placeholder="Option label"
                          />
                          <Input
                            className="w-32"
                            type="number"
                            inputMode="decimal"
                            step="0.01"
                            value={newOptionPrice}
                            onChange={(e) => setNewOptionPrice(e.target.value)}
                            placeholder="Price"
                          />
                          <Button type="button" onClick={handleAddOption} size="sm">
                            <Plus className="h-4 w-4 mr-1" />
                            Add
                          </Button>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Set individual prices for each option
                        </p>
                      </div>

                      {/* Options List */}
                      {options.length > 0 && (
                        <div className="mt-4 space-y-2">
                          <div className="text-xs text-muted-foreground mb-2">Added Options:</div>
                          {options.map((option, index) => (
                            <div
                              key={option.option_value || `${index}-${option.option_label}`}
                              className="flex items-center gap-2 p-2 bg-accent/50 rounded"
                            >
                              <GripVertical className="h-4 w-4 text-muted-foreground" />
                              <div className="flex-1">
                                <span className="font-medium">{option.option_label}</span>
                                {option.price_modifier > 0 && (
                                  <span className="ml-2 text-sm text-muted-foreground">
                                    ${option.price_modifier.toFixed(2)}
                                  </span>
                                )}
                              </div>
                              {Boolean(option.is_default) && (
                                <Badge variant="default">Default</Badge>
                              )}
                              {!option.is_default && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleSetDefault(index)}
                                >
                                  Set Default
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRemoveOption(index)}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={createComponentMutation.isPending}>
              {createComponentMutation.isPending ? "Adding..." : "Add Component"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
